import { api } from '../services/axiosConfig';
import { ShareScope } from '../types';

/** How far along a fit is. `null` means nobody has rated it yet — an
 * honest gap, never rendered as if it were an endorsement. */
export type FittingStatus = 'wip' | 'testing' | 'working';

export interface FittingTag {
  /** Free text since tagging became a member action — 'wh_class' and 'site'
   * are the two the app itself reads back, but a corp invents its own
   * ('activity', 'doctrine', whatever) and the DB CHECK is long gone. */
  tag_type: string;
  tag_value: string;
  recommendation: 'minimum' | 'recommended';
  /** False for a tag someone other than the fitting's owner or an admin
   * proposed, until a second distinct member endorses the exact same tag.
   * The site/class "what to bring" pages never read an unapproved tag —
   * this is only ever shown, greyed, on the fit itself. */
  approved: boolean;
}

export interface FittingSummary {
  id: number;
  name: string;
  /** Null for a hull ESI no longer resolves (usually a CCP rename) — the
   * card falls back to the name rather than requesting a broken image. */
  shipTypeId: number | null;
  shipName: string;
  createdAt: string;
  /** Sharing is the owner's call — only `isMine` fits can be re-scoped. */
  shareScope: ShareScope;
  shareGroupId: number | null;
  isMine: boolean;
  ownerName: string | null;
  status: FittingStatus | null;
  tags: FittingTag[];
  /** Computed tank/offense, sent with the LIST so cards can be compared
   * without opening each fit. Null when the hull didn't resolve. */
  stats: FittingStats | null;
}

export interface FittingItem {
  type_id: number;
  flag: number;
  quantity: number;
  name: string;
}

export interface FittingStats {
  tank: {
    shield: { hp: number; ehp: number; em: number; th: number; kin: number; exp: number };
    armor: { hp: number; ehp: number; em: number; th: number; kin: number; exp: number };
    hull: { hp: number; ehp: number; em: number; th: number; kin: number; exp: number };
    totalEhp: number;
  };
  offense: {
    turretDps: number;
    launcherDps: number;
    droneDps: number;
    totalDps: number;
    turretHasAmmo: boolean;
    launcherHasAmmo: boolean;
    turretAmmoName: string | null;
    launcherAmmoName: string | null;
    droneList: { typeId: number; name: string; qty: number; dps: number }[];
    turretCount: number;
    launcherCount: number;
  };
  navigation: {
    maxVelocity: number;
    abVelocity: number | null;
    mwdVelocity: number | null;
    signatureRadius: number;
    alignTime: string | null;
    warpSpeed: number | null;
    mass: number;
  };
  targeting: { maxTargetRange: number; scanResolution: number; maxLockedTargets: number };
  capacitor: { capacity: number; rechargeTime: number };
  ewar: { neutCount: number; neutAmount: number; webCount: number; webStrength: number; tpCount: number; tpStrength: number; nosCount: number };
  slots: { high: number; mid: number; low: number; rig: number; droneBandwidth: number; droneBay: number; cpuOutput: number; pgOutput: number };
}

/** Jita 4-4 valuation, broken down the way an FC asks for it. Null when
 * the market lookup failed — pricing degrades without taking the fit with
 * it (see services/fitting/fittingIsk.ts). */
export interface FittingIsk {
  ship: number;
  modules: number;
  drones: number;
  ammo: number;
  total: number;
  incomplete: boolean;
}

export interface FittingDetail {
  id: number;
  name: string;
  shipTypeId: number | null;
  shipName: string;
  eftRaw: string;
  items: FittingItem[];
  stats: FittingStats | null;
  isk: FittingIsk | null;
  shareScope: ShareScope;
  shareGroupId: number | null;
  isMine: boolean;
  status: FittingStatus | null;
  tags: FittingTagRow[];
}

export const fetchFittings = () => api.get<FittingSummary[]>('/api/fittings').then((r) => r.data);

export const fetchFitting = (id: number) => api.get<FittingDetail>(`/api/fittings/${id}`).then((r) => r.data);

/** A tag as the import form sends it — no id yet, and no author: the server
 * stamps both. */
export interface NewFittingTag {
  tagType: string;
  tagValue: string;
  recommendation?: 'minimum' | 'recommended';
}

/** Share scope chosen on the way in, same options and same meaning as the
 * Data Vault's own (private/group/corp/alliance/public) — it scopes just
 * this one fitting, not anything else the uploader owns. Omitted entirely
 * defaults to private server-side, same as it always has. */
export const createFitting = (
  name: string,
  eftText: string,
  tags: NewFittingTag[] = [],
  shareScope?: ShareScope,
  shareGroupId?: number | null,
) => api.post<{ id: number }>('/api/fittings', { name, eftText, tags, shareScope, shareGroupId }).then((r) => r.data);

export const deleteFitting = (id: number) => api.delete(`/api/fittings/${id}`);

export const updateFittingShare = (id: number, shareScope: ShareScope, shareGroupId: number | null) =>
  api.patch(`/api/fittings/${id}`, { shareScope, shareGroupId });

/** `null` clears the rating back to unrated. Owner-only, server-enforced. */
export const updateFittingStatus = (id: number, status: FittingStatus | null) =>
  api.patch(`/api/fittings/${id}`, { status });

export interface TaggedFitting {
  id: number;
  name: string;
  ship_name: string;
  /** Null for a hull EVE no longer resolves (a CCP rename); the UI falls
   * back to the name rather than requesting a broken image. */
  ship_type_id: number | null;
  status: FittingStatus | null;
  recommendation: 'minimum' | 'recommended';
}

export const fetchFittingsByClass = (whClass: string) =>
  api.get<TaggedFitting[]>(`/api/fittings/by-class/${encodeURIComponent(whClass)}`).then((r) => r.data);

export const fetchFittingsBySite = (siteName: string) =>
  api.get<TaggedFitting[]>(`/api/fittings/by-site/${encodeURIComponent(siteName)}`).then((r) => r.data);

export const fetchFittingRecommendations = (whClass: string, activity: string, siteName: string) =>
  api.get<TaggedFitting[]>('/api/fittings/recommendations', { params: { whClass, activity, siteName } }).then((r) => r.data);

/** A tag as stored, with who put it there. */
export interface FittingTagRow extends FittingTag {
  id: number;
  fitting_id: number;
  created_by_character_id: number;
  created_by_name: string | null;
  /** How many distinct members have endorsed this exact tag — meaningless
   * once `approved` is true (the vote already did its job), and always at
   * least 1 for a pending tag: the proposer's own. */
  endorsement_count: number;
}

/** Every tag already in use across the corp, most-used first. Offered as
 * quick picks so everyone converges on one spelling of "ratting" rather
 * than five. */
export interface TagVocabularyEntry {
  tag_type: string;
  tag_value: string;
  uses: number;
}

export const fetchTagVocabulary = () =>
  api.get<TagVocabularyEntry[]>('/api/fitting-tags/vocabulary').then((r) => r.data);

export const addFittingTag = (
  fittingId: number,
  tagType: string,
  tagValue: string,
  recommendation: 'minimum' | 'recommended',
) =>
  api
    .post<FittingTagRow[]>(`/api/fittings/${fittingId}/tags`, { tagType, tagValue, recommendation })
    .then((r) => r.data);

export const removeFittingTag = (fittingId: number, tagId: number) =>
  api.delete<FittingTagRow[]>(`/api/fittings/${fittingId}/tags/${tagId}`).then((r) => r.data);
